import React, { useState, useEffect, useCallback, useRef } from "react";
import { getRequest, putRequest } from "../../utils/httpRequest";
import { colors, pageHeaderStyles } from "../AgenticPaymentAirdrop/styles";
import { HelpTitle } from "../AdminHeaderHelp";

// Part of the store payment fee given to a store, per store. Off for every
// store until an admin turns it on here; the share can't exceed the fee.
const PAGE_SIZE = 25;

const card = {
  background: colors.white, border: `1px solid ${colors.border}`,
  borderRadius: "12px", padding: "20px", marginBottom: "20px",
};
const input = {
  padding: "9px 12px", border: "1px solid #ccc", borderRadius: "6px",
  fontSize: "14px", boxSizing: "border-box",
};
const btn = (bg, disabled) => ({
  display: "inline-flex", alignItems: "center", justifyContent: "center", minHeight: "34px",
  background: bg, color: "#fff", border: "none", borderRadius: "6px", padding: "7px 14px",
  fontSize: "13px", fontWeight: 700, cursor: disabled ? "not-allowed" : "pointer", opacity: disabled ? 0.6 : 1,
});
const ghostBtn = {
  display: "inline-flex", alignItems: "center", minHeight: "34px", background: "#e2e3e5", color: "#333",
  border: "none", borderRadius: "6px", padding: "7px 14px", fontSize: "13px", fontWeight: 600, cursor: "pointer",
};
const notice = (ok) => ({
  background: ok ? "#d4edda" : "#f8d7da", color: ok ? "#155724" : "#721c24",
  border: `1px solid ${ok ? "#c3e6cb" : "#f5c6cb"}`, borderRadius: "6px",
  padding: "10px 12px", fontSize: "13px", marginBottom: "16px",
});
const th = {
  textAlign: "left", fontSize: "11px", fontWeight: 700, textTransform: "uppercase",
  letterSpacing: "0.4px", color: colors.gray600, padding: "10px 12px", borderBottom: `1px solid ${colors.border}`,
};
const td = { padding: "12px", fontSize: "13px", borderBottom: `1px solid ${colors.border}`, verticalAlign: "top" };

const pct = (n) => `${Number(Number(n || 0).toFixed(2))}%`;
const money = (n) => Number(n).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const badge = (on) => (
  <span style={{
    fontSize: "10px", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.4px",
    background: on ? "#d4edda" : "#e9ecef", color: on ? "#155724" : "#495057",
    border: `1px solid ${on ? "#c3e6cb" : "#dee2e6"}`, borderRadius: "10px", padding: "2px 8px",
  }}>
    {on ? "On" : "Off"}
  </span>
);

const StoreFeeShare = ({ pageUrl = "/admin/store_fee_share", feesUrl = "/admin/agentic_payment_fees" }) => {
  const url = (action) => `${pageUrl.replace(/\/$/, "")}/${action}`;

  const [feePct, setFeePct] = useState(null);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [query, setQuery] = useState("");
  const [enabledOnly, setEnabledOnly] = useState(false);
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState(null); // { ok, text }

  // Inline editor for one store at a time.
  const [editId, setEditId] = useState(null);
  const [editEnabled, setEditEnabled] = useState(false);
  const [editPct, setEditPct] = useState("");
  const [confirming, setConfirming] = useState(false);
  const [saving, setSaving] = useState(false);

  // Debounce the search box so typing doesn't fire a request per key.
  const debounce = useRef(null);
  useEffect(() => {
    clearTimeout(debounce.current);
    debounce.current = setTimeout(() => { setPage(1); setQuery(search.trim()); }, 300);
    return () => clearTimeout(debounce.current);
  }, [search]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: String(page), limit: String(PAGE_SIZE) });
      if (query) params.set("search", query);
      if (enabledOnly) params.set("enabled_only", "true");
      const res = await getRequest(`${url("store_fee_shares")}?${params.toString()}`);
      if (res && res.success) {
        setFeePct(res.data.feePct);
        setItems(res.data.items || []);
        setTotal(res.data.total || 0);
      } else {
        setMsg({ ok: false, text: (res && res.error) || "Couldn't load stores. Try again." });
      }
    } catch (err) {
      setMsg({ ok: false, text: err?.data?.error || "Couldn't load stores. Try again." });
    } finally {
      setLoading(false);
    }
  }, [pageUrl, page, query, enabledOnly]);

  useEffect(() => { load(); }, [load]);

  const openEditor = (s) => {
    setMsg(null);
    setEditId(s.id);
    setEditEnabled(s.feeShareEnabled);
    setEditPct(s.feeSharePct > 0 ? String(s.feeSharePct) : "");
    setConfirming(false);
  };
  const closeEditor = () => { setEditId(null); setConfirming(false); };

  const pctNum = Number(editPct);
  const pctError = (() => {
    if (!editEnabled) return null;
    if (editPct.trim() === "" || !Number.isFinite(pctNum)) return "Enter a percent";
    if (pctNum <= 0) return "Enter more than 0%";
    if (feePct != null && pctNum > feePct) return `Can't be more than the store fee (${pct(feePct)})`;
    if (!/^\d+(\.\d{1,2})?$/.test(editPct.trim())) return "Use 2 decimals at most";
    return null;
  })();

  const save = async (store) => {
    setSaving(true);
    setMsg(null);
    const body = { store_id: store.id, enabled: editEnabled };
    if (editEnabled) body.share_pct = pctNum;
    try {
      const res = await putRequest(url("update_store_fee_share"), body);
      if (res && res.success) {
        const updated = res.data.store;
        setFeePct(res.data.feePct);
        setItems((prev) => prev.map((s) => (s.id === updated.id ? updated : s)));
        closeEditor();
        setMsg({
          ok: true,
          text: updated.feeShareEnabled
            ? `${updated.name} now gets ${pct(updated.feeSharePct)} of every payment.`
            : `${updated.name} no longer gets part of the fee.`,
        });
      } else {
        setMsg({ ok: false, text: (res && res.error) || "Couldn't save. Try again." });
      }
    } catch (err) {
      setMsg({ ok: false, text: err?.data?.error || "Couldn't save. Try again." });
    } finally {
      setSaving(false);
    }
  };

  // What a 100 payment looks like with the share being edited.
  const example = () => {
    if (feePct == null) return null;
    const share = editEnabled && !pctError ? pctNum : 0;
    return (
      <div style={{ fontSize: "12px", color: colors.gray700, marginTop: "8px", lineHeight: 1.6 }}>
        On a 100.00 payment: customer pays <strong>{money(100 + feePct)}</strong>,
        store receives <strong>{money(100 + share)}</strong>
        {share > 0 && <> (100.00 + {money(share)} share)</>},
        BananaCrystal keeps <strong>{money(feePct - share)}</strong>.
      </div>
    );
  };

  const pages = Math.max(Math.ceil(total / PAGE_SIZE), 1);

  return (
    <div style={{ paddingBottom: "24px", maxWidth: "1100px" }}>
      <div style={{ ...pageHeaderStyles.container, marginBottom: "20px", borderRadius: "12px" }}>
        <h1 style={pageHeaderStyles.title}>Store Fee Share</h1>
      </div>

      <div style={card}>
        <div style={{ display: "flex", flexWrap: "wrap", gap: "24px", alignItems: "center" }}>
          <div>
            <div style={{ fontSize: "12px", color: colors.gray600 }}>Store payment fee, paid by the customer on top of each store payment</div>
            <div style={{ fontSize: "22px", fontWeight: 700, color: colors.primary }}>{feePct == null ? "…" : pct(feePct)}</div>
          </div>
          <div style={{ flex: "1 1 360px", fontSize: "12px", color: colors.gray600, lineHeight: 1.6 }}>
            Turn on a fee share to give a store part of this fee on every payment. BananaCrystal keeps the rest.
            It&apos;s off for every store by default and can&apos;t be more than the fee. To change the fee itself, go to
            the <a href={feesUrl} style={{ color: colors.primary, fontWeight: 600 }}>Fees page</a>.
          </div>
        </div>
      </div>

      {msg && <div style={notice(msg.ok)}>{msg.text}</div>}

      <div style={card}>
        <div style={{ display: "flex", gap: "16px", alignItems: "center", flexWrap: "wrap", marginBottom: "14px" }}>
          <input
            style={{ ...input, flex: "1 1 320px" }}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search store name, @handle or business name"
          />
          <label style={{ fontSize: "13px", color: "#555", display: "inline-flex", alignItems: "center", gap: "6px", cursor: "pointer" }}>
            <input type="checkbox" checked={enabledOnly} onChange={(e) => { setPage(1); setEnabledOnly(e.target.checked); }} />
            Only stores with a fee share
          </label>
        </div>

        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <th style={th}>Store</th>
                <th style={th}>Store status</th>
                <th style={th}><HelpTitle title="Fee share" help="The part of the payment fee this store gets. It can't be more than the current fee." /></th>
                <th style={th}><HelpTitle title="BananaCrystal keeps" help="The payment fee minus the store's share." /></th>
                <th style={th}>Last changed by</th>
                <th style={th} />
              </tr>
            </thead>
            <tbody>
              {!loading && items.length === 0 && (
                <tr><td style={{ ...td, color: colors.gray600 }} colSpan={6}>{query ? `No stores match "${query}".` : "No stores yet."}</td></tr>
              )}
              {items.map((s) => (
                <React.Fragment key={s.id}>
                  <tr style={editId === s.id ? { background: "#f7f8fa" } : undefined}>
                    <td style={td}>
                      <div style={{ fontWeight: 600, color: colors.primary }}>{s.name}</div>
                      <div style={{ color: colors.gray600, fontSize: "12px" }}>
                        @{s.storeUsername}{s.businessName ? ` · ${s.businessName}` : ""}
                      </div>
                    </td>
                    <td style={td}>{s.status}</td>
                    <td style={td}>
                      {badge(s.feeShareEnabled)}
                      {s.feeShareEnabled && <span style={{ marginLeft: "8px", fontWeight: 600 }}>{pct(s.effectiveSharePct)}</span>}
                      {s.feeShareEnabled && s.effectiveSharePct < s.feeSharePct && (
                        <div style={{ color: "#9a3412", fontSize: "11px", marginTop: "4px" }}>
                          Set to {pct(s.feeSharePct)}, capped at the current fee
                        </div>
                      )}
                    </td>
                    <td style={td}>{feePct == null ? "…" : pct(feePct - s.effectiveSharePct)}</td>
                    <td style={{ ...td, fontSize: "12px", color: colors.gray600 }}>
                      {s.feeShareUpdatedAt ? (
                        <>
                          <div>{new Date(s.feeShareUpdatedAt).toLocaleString()}</div>
                          <div>{s.feeShareUpdatedBy}</div>
                        </>
                      ) : "Not set yet"}
                    </td>
                    <td style={{ ...td, textAlign: "right" }}>
                      {editId !== s.id && (
                        <button type="button" style={btn(colors.primary, false)} onClick={() => openEditor(s)}>Edit</button>
                      )}
                    </td>
                  </tr>
                  {editId === s.id && (
                    <tr style={{ background: "#f7f8fa" }}>
                      <td style={td} colSpan={6}>
                        <div style={{ display: "flex", gap: "20px", alignItems: "flex-start", flexWrap: "wrap" }}>
                          <label style={{ fontSize: "13px", fontWeight: 600, color: "#555", display: "inline-flex", alignItems: "center", gap: "8px", paddingTop: "8px", cursor: "pointer" }}>
                            <input type="checkbox" checked={editEnabled} disabled={saving}
                                   onChange={(e) => { setConfirming(false); setEditEnabled(e.target.checked); }} />
                            Give this store part of the fee
                          </label>
                          {editEnabled && (
                            <div>
                              <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                                <input
                                  style={{ ...input, width: "110px", borderColor: pctError ? "#dc3545" : "#ccc" }}
                                  type="number" min="0.01" step="0.01" max={feePct ?? undefined}
                                  value={editPct} disabled={saving}
                                  onChange={(e) => { setConfirming(false); setEditPct(e.target.value); }}
                                  placeholder="0.50"
                                />
                                <span style={{ fontSize: "13px", color: "#555" }}>Share, in % of each payment (max {feePct == null ? "…" : pct(feePct)})</span>
                              </div>
                              {pctError && <div style={{ color: "#dc3545", fontSize: "11px", marginTop: "4px" }}>{pctError}</div>}
                            </div>
                          )}
                        </div>
                        {example()}
                        <div style={{ display: "flex", gap: "10px", marginTop: "14px", alignItems: "center", flexWrap: "wrap" }}>
                          {!confirming ? (
                            <>
                              <button type="button" style={ghostBtn} onClick={closeEditor} disabled={saving}>Cancel</button>
                              <button type="button" style={btn(colors.primary, !!pctError)} disabled={!!pctError}
                                      onClick={() => setConfirming(true)}>
                                Save
                              </button>
                            </>
                          ) : (
                            <>
                              <span style={{ fontWeight: 700, color: "#9a3412", fontSize: "13px" }}>
                                {editEnabled
                                  ? `Give ${s.name} ${pct(pctNum)} of every payment from now on?`
                                  : `Stop giving ${s.name} part of the fee?`}
                              </span>
                              <button type="button" style={ghostBtn} onClick={() => setConfirming(false)} disabled={saving}>Back</button>
                              <button type="button" style={btn("#c0392b", saving)} disabled={saving} onClick={() => save(s)}>
                                {saving ? "Saving…" : "Confirm & save"}
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>

        {pages > 1 && (
          <div style={{ display: "flex", gap: "10px", alignItems: "center", justifyContent: "flex-end", marginTop: "14px", fontSize: "13px" }}>
            <button type="button" style={ghostBtn} disabled={page <= 1 || loading} onClick={() => setPage(page - 1)}>Previous</button>
            <span>Page {page} of {pages} · {total} stores</span>
            <button type="button" style={ghostBtn} disabled={page >= pages || loading} onClick={() => setPage(page + 1)}>Next</button>
          </div>
        )}
      </div>
    </div>
  );
};

export default StoreFeeShare;
